import React, { useState } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import toast from 'react-hot-toast';

export default function CoachPerformance({ allAcademyStudents }) {
    const [searchQuery, setSearchQuery] = useState('');
    const [expandedStudent, setExpandedStudent] = useState(null);
    const [feedbacks, setFeedbacks] = useState({}); // Stores local feedback text state

    const handleSaveFeedback = async (email, text) => {
        const loading = toast.loading("Saving coach feedback...");
        try {
            await updateDoc(doc(db, "users", email), {
                coachFeedback: text
            });
            toast.success("Coach feedback updated successfully!", { id: loading });
        } catch (err) {
            console.error(err);
            toast.error("Failed to save feedback.", { id: loading });
        }
    };

    const filteredStudents = useMemo(() => {
        return allAcademyStudents.filter(s => {
            const q = searchQuery.toLowerCase();
            const nameMatch = s.name?.toLowerCase().includes(q);
            const emailMatch = s.email?.toLowerCase().includes(q);
            return nameMatch || emailMatch;
        });
    }, [allAcademyStudents, searchQuery]);

    // Use memoized helper in react function (since useMemo needs imports, let's just make it simple using React.useMemo)
    function useMemo(factory, deps) {
        return React.useMemo(factory, deps);
    }

    return (
        <div style={{ background: 'var(--bg-primary)', padding: '30px', borderRadius: '20px', boxShadow: 'var(--panel-shadow)', border: '1px solid rgba(255,255,255,0.55)', width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '15px' }}>
                <div>
                    <h2 style={{ color: 'var(--text-primary)', margin: 0, fontSize: '20px' }}>📊 Student Progress Tracker</h2>
                    <p style={{ color: 'var(--text-secondary)', margin: '4px 0 0 0', fontSize: '13px', fontWeight: '500' }}>Review individual performance stats and write custom feedback notes.</p>
                </div>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                    <input 
                        type="text" 
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search student name or email..."
                        style={{ padding: '10px 14px', paddingLeft: '32px', background: '#e0e5ec', border: '1px solid rgba(255, 255, 255, 0.45)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '13px', width: '250px', outline: 'none', boxShadow: 'var(--neu-shadow-inset)' }}
                    />
                    <span style={{ position: 'absolute', left: '10px', color: 'var(--text-secondary)', fontSize: '13px', pointerEvents: 'none' }}>🔍</span>
                </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginTop: '25px' }}>
                {filteredStudents.length === 0 ? (
                    <div style={{ color: 'var(--text-secondary)', textAlign: 'center', padding: '40px', background: '#e0e5ec', borderRadius: '12px', boxShadow: 'var(--neu-shadow-inset)', border: '1px solid rgba(255,255,255,0.45)' }}>
                        📭 No students found matching your criteria.
                    </div>
                ) : (
                    filteredStudents.map((student, sIdx) => {
                        const isExpanded = expandedStudent === student.email;
                        const feedbackText = feedbacks[student.email] !== undefined ? feedbacks[student.email] : (student.coachFeedback || '');
                        
                        return (
                            <div key={student.email || sIdx} style={{ 
                                background: '#e0e5ec', 
                                borderRadius: '16px', 
                                border: '1px solid rgba(255,255,255,0.55)', 
                                boxShadow: isExpanded ? 'var(--panel-shadow-hover)' : 'var(--panel-shadow)',
                                overflow: 'hidden', 
                                transition: 'all 0.25s ease-in-out' 
                            }}>
                                {/* Student Card Header */}
                                <div 
                                    onClick={() => setExpandedStudent(isExpanded ? null : student.email)}
                                    style={{ 
                                        padding: '18px 24px', 
                                        display: 'flex', 
                                        justifyContent: 'space-between', 
                                        alignItems: 'center', 
                                        cursor: 'pointer',
                                        background: isExpanded ? 'var(--btn-hover)' : 'transparent',
                                        borderLeft: '5px solid #a855f7',
                                        userSelect: 'none'
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                        <div style={{ fontSize: '24px', width: '45px', height: '45px', background: '#e0e5ec', display: 'flex', justifyContent: 'center', alignItems: 'center', borderRadius: '50%', boxShadow: '4px 4px 8px rgba(163,177,198,0.4), -4px -4px 8px rgba(255,255,255,0.85)', border: '1px solid rgba(255,255,255,0.55)' }}>
                                            {student.avatar || '👤'}
                                        </div>
                                        <div>
                                            <div style={{ fontSize: '15px', color: 'var(--text-primary)', fontWeight: 'bold' }}>
                                                {student.name || 'Anonymous'} {student.online && <span style={{ color: '#10b981', fontSize: '11px', marginLeft: '5px' }}>🟢 Online</span>}
                                            </div>
                                            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: '500', marginTop: '2px' }}>{student.email}</div>
                                        </div>
                                    </div>

                                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                        <div style={{ background: 'var(--bg-primary)', padding: '5px 12px', borderRadius: '8px', color: '#a855f7', fontWeight: 'bold', fontSize: '11px', border: '1px solid rgba(255,255,255,0.6)', boxShadow: '2px 2px 5px rgba(163,177,198,0.25), -2px -2px 5px rgba(255,255,255,0.7)' }}>
                                            🏆 ELO: {student.elo || 1200}
                                        </div>
                                        <span style={{ color: 'var(--text-secondary)', fontSize: '13px', transform: isExpanded ? 'rotate(180deg)' : 'none', transition: '0.3s' }}>▼</span>
                                    </div>
                                </div>

                                {/* Student Expanded Details */}
                                {isExpanded && (
                                    <div style={{ 
                                        padding: '24px 28px', 
                                        background: '#e0e5ec', 
                                        borderTop: '1px solid rgba(255,255,255,0.45)', 
                                        boxShadow: 'inset 3px 3px 8px rgba(163,177,198,0.2), inset -3px -3px 8px rgba(255,255,255,0.75)',
                                        display: 'flex', 
                                        flexDirection: 'column', 
                                        gap: '24px' 
                                    }}>
                                        {/* Stats Row */}
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '15px' }}>
                                            <div style={{ background: 'var(--bg-primary)', padding: '12px', borderRadius: '12px', textAlign: 'center', border: '1px solid rgba(255,255,255,0.55)', boxShadow: '3px 3px 8px rgba(163,177,198,0.3), -3px -3px 8px rgba(255,255,255,0.75)' }}>
                                                <div style={{ fontSize: '20px', marginBottom: '4px' }}>⭐</div>
                                                <div style={{ fontSize: '16px', color: 'var(--text-primary)', fontWeight: 'bold' }}>{student.stars || 0}</div>
                                                <div style={{ fontSize: '10px', color: 'var(--text-secondary)', fontWeight: 'bold', textTransform: 'uppercase' }}>Stars</div>
                                            </div>
                                            <div style={{ background: 'var(--bg-primary)', padding: '12px', borderRadius: '12px', textAlign: 'center', border: '1px solid rgba(255,255,255,0.55)', boxShadow: '3px 3px 8px rgba(163,177,198,0.3), -3px -3px 8px rgba(255,255,255,0.75)' }}>
                                                <div style={{ fontSize: '20px', marginBottom: '4px' }}>🪙</div>
                                                <div style={{ fontSize: '16px', color: 'var(--text-primary)', fontWeight: 'bold' }}>{student.coins || 0}</div>
                                                <div style={{ fontSize: '10px', color: 'var(--text-secondary)', fontWeight: 'bold', textTransform: 'uppercase' }}>Coins</div>
                                            </div>
                                            <div style={{ background: 'var(--bg-primary)', padding: '12px', borderRadius: '12px', textAlign: 'center', border: '1px solid rgba(255,255,255,0.55)', boxShadow: '3px 3px 8px rgba(163,177,198,0.3), -3px -3px 8px rgba(255,255,255,0.75)' }}>
                                                <div style={{ fontSize: '20px', marginBottom: '4px' }}>🎯</div>
                                                <div style={{ fontSize: '16px', color: 'var(--text-primary)', fontWeight: 'bold' }}>{student.completedMissions?.length || 0}</div>
                                                <div style={{ fontSize: '10px', color: 'var(--text-secondary)', fontWeight: 'bold', textTransform: 'uppercase' }}>Missions</div>
                                            </div>
                                            <div style={{ background: 'var(--bg-primary)', padding: '12px', borderRadius: '12px', textAlign: 'center', border: '1px solid rgba(255,255,255,0.55)', boxShadow: '3px 3px 8px rgba(163,177,198,0.3), -3px -3px 8px rgba(255,255,255,0.75)' }}>
                                                <div style={{ fontSize: '20px', marginBottom: '4px' }}>⏱️</div>
                                                <div style={{ fontSize: '16px', color: 'var(--text-primary)', fontWeight: 'bold' }}>{student.activeMinutes || 0}m</div>
                                                <div style={{ fontSize: '10px', color: 'var(--text-secondary)', fontWeight: 'bold', textTransform: 'uppercase' }}>Time Spent</div>
                                            </div>
                                        </div>

                                        {/* Coach Private Notes / Report Card */}
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                            <label style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                ✍️ Coach Feedback / Report Card (Visible to Student)
                                            </label>
                                            <textarea 
                                                value={feedbackText}
                                                onChange={(e) => setFeedbacks(prev => ({ ...prev, [student.email]: e.target.value }))}
                                                placeholder="Write specific recommendations, areas of improvement, or praises here..."
                                                rows="3"
                                                style={{ 
                                                    padding: '12px 16px', 
                                                    background: '#e0e5ec', 
                                                    border: '1px solid rgba(255, 255, 255, 0.45)', 
                                                    borderRadius: '12px', 
                                                    color: 'var(--text-primary)', 
                                                    fontSize: '13px', 
                                                    outline: 'none', 
                                                    boxShadow: 'var(--neu-shadow-inset)',
                                                    resize: 'vertical',
                                                    fontFamily: 'inherit'
                                                }}
                                            />
                                            <button 
                                                onClick={() => handleSaveFeedback(student.email, feedbackText)}
                                                style={{ padding: '8px 16px', background: 'var(--bg-primary)', border: '1px solid rgba(255,255,255,0.6)', color: '#a855f7', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', fontSize: '12px', boxShadow: '2px 2px 5px rgba(163,177,198,0.25), -2px -2px 5px rgba(255,255,255,0.7)', transition: 'all 0.2s', width: 'fit-content', marginTop: '4px' }}
                                            >
                                                💾 Save Feedback
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
}
